import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Users, 
  Search, 
  Play, 
  Clock, 
  CheckCircle, 
  AlertCircle,
  User,
  Building,
  ArrowRight,
  Loader2,
  History,
  FileText,
  ListChecks
} from 'lucide-react';

export default function FacilitatorDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [participants, setParticipants] = useState([]);
  const [recentSessions, setRecentSessions] = useState([]);
  const [sessionTemplates, setSessionTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showStartDialog, setShowStartDialog] = useState(false);
  const [selectedParticipant, setSelectedParticipant] = useState(null);
  const [selectedAssessmentType, setSelectedAssessmentType] = useState('fear');
  const [selectedTemplateId, setSelectedTemplateId] = useState('none');
  const [startingSession, setStartingSession] = useState(false);
  
  useEffect(() => {
    fetchData();
  }, []);
  
  const fetchData = async () => {
    try {
      setLoading(true);
      const [participantsRes, sessionsRes, templatesRes] = await Promise.all([
        axios.get('/api/facilitator/participants'),
        axios.get('/api/facilitator/sessions?status=in_progress'),
        axios.get('/api/facilitator/session-templates')
      ]);
      
      setParticipants(participantsRes.data.participants || []);
      setRecentSessions(sessionsRes.data.sessions || []);
      setSessionTemplates(templatesRes.data.templates || []);
    } catch (error) {
      toast.error('Failed to load data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleStartSession = (participant) => {
    setSelectedParticipant(participant);
    setSelectedTemplateId('none');
    
    // Default to fear if no EDGE diagnostics, otherwise archetype
    if (participant.fearAssessmentsCompleted === 0) {
      setSelectedAssessmentType('fear');
    } else if (participant.archetypeAssessmentsCompleted === 0) {
      setSelectedAssessmentType('archetype');
    } else {
      setSelectedAssessmentType('fear');
    }
    
    setShowStartDialog(true);
  };
  
  const handleResumeSession = (participant) => {
    navigate(`/admin/facilitator/session/${participant.activeFacilitatedSessionId}`);
  };
  
  const startNewSession = async () => {
    if (!selectedParticipant) return;
    
    setStartingSession(true);
    try {
      const response = await axios.post('/api/facilitator/sessions', {
        participantId: selectedParticipant.id,
        assessmentType: selectedAssessmentType
      });
      
      setShowStartDialog(false);
      toast.success('Session started!');
      navigate(`/admin/facilitator/session/${response.data.session.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start session');
    } finally {
      setStartingSession(false);
    }
  };
  
  const filteredParticipants = participants.filter(p => {
    const searchLower = searchQuery.toLowerCase();
    return (
      p.email?.toLowerCase().includes(searchLower) ||
      p.firstName?.toLowerCase().includes(searchLower) ||
      p.lastName?.toLowerCase().includes(searchLower) ||
      p.company?.toLowerCase().includes(searchLower)
    );
  });
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }
  
  return (
    <div data-testid="facilitator-dashboard">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-semibold">Facilitator Dashboard</h1>
          <p className="text-muted-foreground">Manage live facilitated sessions</p>
        </div>
        <Badge variant="secondary">Live Sessions</Badge>
      </div>
      
      <div className="space-y-8">
      
      {/* Active Sessions */}
      {recentSessions.length > 0 && (
        <Card className="border-warning/50 bg-warning/5">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-warning">
              <Clock className="w-5 h-5" />
              Active Sessions
            </CardTitle>
            <CardDescription>
              Continue an in-progress facilitated session
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {recentSessions.map(session => (
                <div 
                  key={session.id}
                  className="flex items-center justify-between p-4 bg-card border border-border rounded-lg"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-warning/10 flex items-center justify-center">
                      <User className="w-5 h-5 text-warning" />
                    </div>
                    <div>
                      <p className="font-medium">
                        {session.participant?.firstName} {session.participant?.lastName}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {session.type === 'fear' ? 'EDGE Diagnostic' : session.type === 'archetype' ? 'CORE Diagnostic' : `${session.type} Assessment`} • {session.questionCount || 0} questions
                      </p>
                    </div>
                  </div>
                  <Button onClick={() => navigate(`/admin/facilitator/session/${session.id}`)}>
                    Continue <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Participants Section */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                Select Participant
              </CardTitle>
              <CardDescription>
                Choose a participant to start a facilitated session
              </CardDescription>
            </div>
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search participants..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
                data-testid="search-participants"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {filteredParticipants.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No participants found</p>
            </div>
          ) : (
            <div className="grid gap-4">
              {filteredParticipants.map(participant => (
                <div 
                  key={participant.id}
                  className="flex items-center justify-between p-4 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors"
                  data-testid={`participant-${participant.id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                      <User className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium">
                        {participant.firstName || participant.lastName 
                          ? `${participant.firstName} ${participant.lastName}`.trim()
                          : participant.email
                        }
                      </p>
                      <div className="flex items-center gap-3 text-sm text-muted-foreground">
                        <span>{participant.email}</span>
                        {participant.company && (
                          <>
                            <span>•</span>
                            <span className="flex items-center gap-1">
                              <Building className="w-3 h-3" />
                              {participant.company}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    {/* Assessment Status Badges */}
                    <div className="flex items-center gap-2">
                      <Badge variant={participant.fearAssessmentsCompleted > 0 ? "default" : "outline"}>
                        {participant.fearAssessmentsCompleted > 0 ? (
                          <CheckCircle className="w-3 h-3 mr-1" />
                        ) : (
                          <AlertCircle className="w-3 h-3 mr-1" />
                        )}
                        EDGE
                      </Badge>
                      <Badge variant={participant.archetypeAssessmentsCompleted > 0 ? "default" : "outline"}>
                        {participant.archetypeAssessmentsCompleted > 0 ? (
                          <CheckCircle className="w-3 h-3 mr-1" />
                        ) : (
                          <AlertCircle className="w-3 h-3 mr-1" />
                        )}
                        CORE
                      </Badge>
                    </div>
                    
                    {/* Action Button */}
                    {participant.hasActiveFacilitatedSession ? (
                      <Button 
                        variant="outline"
                        onClick={() => handleResumeSession(participant)}
                        data-testid={`resume-session-${participant.id}`}
                      >
                        <History className="w-4 h-4 mr-1" />
                        Resume
                      </Button>
                    ) : (
                      <Button 
                        onClick={() => handleStartSession(participant)}
                        data-testid={`start-session-${participant.id}`}
                      >
                        <Play className="w-4 h-4 mr-1" />
                        Start Session
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Start Session Dialog */}
      <Dialog open={showStartDialog} onOpenChange={setShowStartDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Start Facilitated Session</DialogTitle>
            <DialogDescription>
              You will conduct this assessment live with the participant
            </DialogDescription>
          </DialogHeader>
          
          {selectedParticipant && (
            <div className="py-4 space-y-4">
              {/* Participant Info */}
              <div className="flex items-center gap-4 p-4 bg-muted/50 rounded-lg">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <User className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <p className="font-medium">
                    {selectedParticipant.firstName || selectedParticipant.lastName 
                      ? `${selectedParticipant.firstName} ${selectedParticipant.lastName}`.trim()
                      : selectedParticipant.email
                    }
                  </p>
                  <p className="text-sm text-muted-foreground">{selectedParticipant.email}</p>
                </div>
              </div>
              
              {/* Assessment Type Selection */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Assessment Type</label>
                <Select value={selectedAssessmentType} onValueChange={setSelectedAssessmentType}>
                  <SelectTrigger data-testid="assessment-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fear">
                      <div className="flex items-center gap-2">
                        EDGE Diagnostic
                        {selectedParticipant.fearAssessmentsCompleted > 0 && (
                          <Badge variant="secondary" className="text-xs">Completed before</Badge>
                        )}
                      </div>
                    </SelectItem>
                    <SelectItem value="archetype">
                      <div className="flex items-center gap-2">
                        CORE Diagnostic
                        {selectedParticipant.archetypeAssessmentsCompleted > 0 && (
                          <Badge variant="secondary" className="text-xs">Completed before</Badge>
                        )}
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
                
                {selectedAssessmentType === 'archetype' && selectedParticipant.fearAssessmentsCompleted === 0 && (
                  <p className="text-sm text-warning flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    Participant hasn't completed EDGE Diagnostic yet
                  </p>
                )}
              </div>

              {/* Session Template Selection */}
              <div className="space-y-2">
                <label className="text-sm font-medium flex items-center gap-2">
                  <ListChecks className="w-4 h-4" />
                  Session Template (Optional)
                </label>
                <Select value={selectedTemplateId} onValueChange={setSelectedTemplateId}>
                  <SelectTrigger data-testid="template-select">
                    <SelectValue placeholder="Select a template..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">
                      <div className="flex items-center gap-2 text-muted-foreground">
                        No template - free-form session
                      </div>
                    </SelectItem>
                    {sessionTemplates
                      .filter(t => t.type === 'general' || t.type === selectedAssessmentType || 
                               (selectedAssessmentType === 'fear' && t.type === 'edge') ||
                               (selectedAssessmentType === 'archetype' && t.type === 'core'))
                      .map((template) => (
                        <SelectItem key={template.id} value={template.id}>
                          <div className="flex items-center gap-2">
                            <FileText className="w-3 h-3" />
                            {template.name}
                            <Badge variant="outline" className="text-xs ml-1">
                              {template.estimatedDuration} min
                            </Badge>
                          </div>
                        </SelectItem>
                      ))
                    }
                  </SelectContent>
                </Select>
                {selectedTemplateId && selectedTemplateId !== 'none' && (
                  <div className="p-3 bg-muted/30 rounded-lg text-sm">
                    {sessionTemplates.find(t => t.id === selectedTemplateId)?.description || 
                     'Template provides a structured session guide'}
                    <p className="text-xs text-muted-foreground mt-1">
                      {sessionTemplates.find(t => t.id === selectedTemplateId)?.steps?.length || 0} steps
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStartDialog(false)}>
              Cancel
            </Button>
            <Button onClick={startNewSession} disabled={startingSession}>
              {startingSession ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Starting...
                </>
              ) : (
                <>
                  <Play className="w-4 h-4 mr-2" />
                  Start Session
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </div>
  );
}
